import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import AvailabilityService from '../../storefront-client-portal/bundle/components/booking/services/AvailabilityService';
import { getDoubleBookingSettings } from '../../../shared/booking/double-booking/employeeSlotAvailability';
import {
  computeStorefrontTotalDurationMinutes,
  prefetchStorefrontAvailability,
} from '../prefetchStorefrontAvailability';
import { generateStorefrontTimeSlots } from '../generateStorefrontTimeSlots';

export function isStorefrontDayOpen(day, companyHours = {}) {
  const dayKey = format(startOfDay(day), 'EEEE').toLowerCase();
  return Boolean(companyHours?.[dayKey]?.isOpen);
}

function getSelectableDaysInMonthGrid({ monthCursor, minDate, maxDate }) {
  const min = startOfDay(minDate);
  const max = startOfDay(maxDate);
  const monthStart = startOfMonth(monthCursor);
  const monthEnd = endOfMonth(monthCursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  return eachDayOfInterval({ start: gridStart, end: gridEnd }).filter((day) => {
    const normalized = startOfDay(day);
    return !isBefore(normalized, min) && !isAfter(normalized, max);
  });
}

function buildCalendarAvailabilityCacheKey({
  companyData,
  service,
  selectedPackage,
  selectedAddons,
  locationId,
  monthCursor,
  minDate,
  maxDate,
}) {
  const addonKey = (selectedAddons || [])
    .map((addon) => addon.id)
    .sort()
    .join(',');
  return [
    companyData?.id || '',
    service?.id || '',
    selectedPackage?.id || '',
    addonKey,
    computeStorefrontTotalDurationMinutes(service, selectedPackage, selectedAddons),
    locationId || '',
    format(startOfMonth(monthCursor), 'yyyy-MM'),
    format(startOfDay(minDate), 'yyyy-MM-dd'),
    format(startOfDay(maxDate), 'yyyy-MM-dd'),
  ].join('|');
}

const pendingCalendarAvailabilityBuilds = new Map();

async function buildStorefrontCalendarAvailabilityMapInternal({
  companyData,
  service,
  selectedPackage = null,
  selectedAddons = [],
  companyHours = {},
  locationId = null,
  location = null,
  customerNoStaffMessage,
  monthCursor,
  minDate,
  maxDate,
}) {
  const availability = new Map();
  const selectableDays = getSelectableDaysInMonthGrid({ monthCursor, minDate, maxDate });

  selectableDays.forEach((day) => {
    const key = format(startOfDay(day), 'yyyy-MM-dd');
    if (!isStorefrontDayOpen(day, companyHours)) {
      availability.set(key, false);
    }
  });

  const openDays = selectableDays.filter((day) => isStorefrontDayOpen(day, companyHours));
  if (!openDays.length || !companyData?.id || !service?.id) {
    return availability;
  }

  const staffPrefetch = await prefetchStorefrontAvailability({
    companyData,
    service,
    date: startOfDay(openDays[0]),
    selectedPackage,
    selectedAddons,
    customerNoStaffMessage,
    locationId,
    location,
    includeAppointments: false,
  });

  const roster = (staffPrefetch.staff || []).filter((member) => !member.isAnyAvailable);

  if (staffPrefetch.error || !roster.length) {
    openDays.forEach((day) => {
      availability.set(format(startOfDay(day), 'yyyy-MM-dd'), false);
    });
    return availability;
  }

  const appointmentsByDay = new Map();
  await Promise.all(
    openDays.map(async (day) => {
      const key = format(startOfDay(day), 'yyyy-MM-dd');
      const appointments = await AvailabilityService.fetchDayAppointments(companyData.id, day);
      appointmentsByDay.set(key, appointments);
    })
  );

  const totalDuration = computeStorefrontTotalDurationMinutes(
    service,
    selectedPackage,
    selectedAddons
  );
  const bookingConflictSettings = getDoubleBookingSettings(companyData);

  openDays.forEach((day) => {
    const key = format(startOfDay(day), 'yyyy-MM-dd');
    const { slots } = generateStorefrontTimeSlots({
      date: startOfDay(day),
      companyHours,
      totalDuration,
      rosterForSlots: roster,
      appointmentsForDay: appointmentsByDay.get(key) || [],
      bookingConflictSettings,
      staff: null,
    });
    availability.set(key, slots.length > 0);
  });

  return availability;
}

/**
 * Returns Map<yyyy-MM-dd, boolean> — true when at least one bookable slot exists.
 */
export async function buildStorefrontCalendarAvailabilityMap(params) {
  const cacheKey = buildCalendarAvailabilityCacheKey(params);
  const pending = pendingCalendarAvailabilityBuilds.get(cacheKey);
  if (pending) {
    return pending;
  }

  const promise = buildStorefrontCalendarAvailabilityMapInternal(params).finally(() => {
    pendingCalendarAvailabilityBuilds.delete(cacheKey);
  });

  pendingCalendarAvailabilityBuilds.set(cacheKey, promise);
  return promise;
}
